import { GoogleGenAI, Modality } from "@google/genai";
import type { GenerateContentResponse } from "@google/genai";

// Type definitions
export type AspectRatio = "1:1" | "3:4" | "4:3" | "9:16" | "16:9";

// Get API key from environment variable
const getApiKey = (): string => {
  const apiKey = import.meta.env.VITE_GOOGLE_AI_API_KEY;
  if (!apiKey) {
    throw new Error("VITE_GOOGLE_AI_API_KEY environment variable not set. Please add it to your .env.local file.");
  }
  return apiKey;
};

// Initialize Google AI with lazy loading
let aiInstance: GoogleGenAI | null = null;

const getAI = (): GoogleGenAI => {
  if (!aiInstance) {
    aiInstance = new GoogleGenAI({ apiKey: getApiKey() });
  }
  return aiInstance;
};

// Helper function to convert File to base64 format for Gemini
const fileToGenerativePart = async (file: File) => {
  const base64EncodedDataPromise = new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
    reader.readAsDataURL(file);
  });
  return {
    inlineData: { data: await base64EncodedDataPromise, mimeType: file.type },
  };
};

// Text Generation Functions

export const generateText = async (prompt: string): Promise<GenerateContentResponse> => {
  const ai = getAI();
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash',
    contents: prompt,
  });
  return response;
};

export const generateTextWithLite = async (prompt: string): Promise<GenerateContentResponse> => {
  const ai = getAI();
  const response = await ai.models.generateContent({
    model: 'gemini-flash-lite-latest',
    contents: prompt,
  });
  return response;
};

export const generateTextWithPro = async (prompt: string): Promise<GenerateContentResponse> => {
  const ai = getAI();
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-pro',
    contents: prompt,
    config: {
      thinkingConfig: { thinkingBudget: 32768 }
    }
  });
  return response;
};

export const generateTextWithSearchGrounding = async (prompt: string): Promise<GenerateContentResponse> => {
  const ai = getAI();
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash',
    contents: prompt,
    config: {
      tools: [{ googleSearch: {} }],
    },
  });
  return response;
};

export const generateTextWithMapsGrounding = async (prompt: string): Promise<GenerateContentResponse> => {
  const ai = getAI();
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash',
    contents: prompt,
    config: {
      tools: [{ googleMaps: {} }],
    },
  });
  return response;
};

// Image Generation and Editing Functions

export const editImage = async (prompt: string, image: File): Promise<string> => {
  const ai = getAI();
  const imagePart = await fileToGenerativePart(image);
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash-image',
    contents: {
      parts: [imagePart, { text: prompt }],
    },
    config: {
      responseModalities: [Modality.IMAGE],
    },
  });

  for (const part of response.candidates[0].content.parts) {
    if (part.inlineData) {
      return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
    }
  }
  throw new Error("No image generated");
};

export const generateImage = async (prompt: string, aspectRatio: AspectRatio = "1:1"): Promise<string> => {
  const ai = getAI();

  // Enhanced prompt for logo generation
  const enhancedPrompt = prompt.toLowerCase().includes('logo')
    ? `${prompt}. Professional, clean design with transparent or white background, suitable for printing on merchandise.`
    : prompt;

  const response = await ai.models.generateImages({
    model: 'imagen-4.0-generate-001',
    prompt: enhancedPrompt,
    config: {
      numberOfImages: 1,
      outputMimeType: 'image/png',
      aspectRatio: aspectRatio,
    },
  });

  if (response.generatedImages && response.generatedImages.length > 0) {
    const base64ImageBytes: string = response.generatedImages[0].image.imageBytes;
    return `data:image/png;base64,${base64ImageBytes}`;
  }
  throw new Error("No image generated");
};

// Design-specific helper functions

export const generateLogoForProduct = async (description: string, productType: string): Promise<string> => {
  const prompt = `Create a professional logo for ${description}. The logo should be suitable for printing on ${productType}. Style: modern, clean, versatile. Background: transparent or white.`;
  return generateImage(prompt, "1:1");
};

export const enhanceImageForPrinting = async (image: File): Promise<string> => {
  const prompt = "Enhance this image for high-quality printing: increase contrast, sharpen details, optimize colors for CMYK printing, ensure high resolution clarity.";
  return editImage(prompt, image);
};

export const removeBackground = async (image: File): Promise<string> => {
  const prompt = "Remove the background from this image completely, leaving only the main subject with a transparent background.";
  return editImage(prompt, image);
};

export const suggestDesignColors = async (productType: string, theme: string): Promise<string[]> => {
  const prompt = `Suggest 5 complementary color combinations (hex codes) for a ${productType} with a ${theme} theme. Return only the hex codes in JSON array format like ["#RRGGBB", "#RRGGBB", ...].`;
  const response = await generateText(prompt);

  try {
    const text = response.text.trim();
    const match = text.match(/\[.*\]/);
    if (match) {
      return JSON.parse(match[0]);
    }
    return ['#3B82F6', '#EC4899', '#10B981', '#FBBF24', '#8B5CF6'];
  } catch (error) {
    console.error('Failed to parse color suggestions:', error);
    return ['#3B82F6', '#EC4899', '#10B981', '#FBBF24', '#8B5CF6'];
  }
};

// Export utility to check if API key is configured
export const isGeminiConfigured = (): boolean => {
  try {
    return !!import.meta.env.VITE_GOOGLE_AI_API_KEY;
  } catch {
    return false;
  }
};